import { useEffect, useState } from "react";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import { firebaseApp } from "../firebase/firebaseApp";
import { useUserClinicMapping } from "./useUserClinicMapping";

export function useRecordingModeConfig({ uid }) {
  const db = getFirestore(firebaseApp);
  const mapping = useUserClinicMapping({ uid });

  const [state, setState] = useState({
    loading: true, // overall: config still being determined
    clinicStudyId: null,
    config: null,
    error: null,
  });

  useEffect(() => {
    if (!uid) {
      setState({
        loading: false,
        clinicStudyId: null,
        config: null,
        error: null,
      });
      return;
    }

    // Wait until mapping is resolved (fast Firestore)
    if (!mapping.resolved) {
      setState({
        loading: true,
        clinicStudyId: null,
        config: null,
        error: null,
      });
      return;
    }

    let alive = true;

    // reset immediately on uid change
    setState({
      loading: true,
      profileResolved: false,
      clinicStudyId: null,
      config: null,
      error: null,
    });

    (async () => {
      try {
        if (!alive) return;

        // At this point we KNOW profile result (even if null)
        const clinicStudyId = mapping.clinicStudyId ?? null;

        if (!clinicStudyId) {
          setState({
            loading: false,
            clinicStudyId: null,
            config: null,
            error: null,
          });
          return;
        }

        const ref = doc(db, "recording_mode_configs", clinicStudyId);
        const snap = await getDoc(ref);

        if (!alive) return;

        if (!snap.exists()) {
          setState({
            loading: false,
            clinicStudyId,
            config: null,
            error: "not_found",
          });
          return;
        }

        setState({
          loading: false,
          clinicStudyId,
          config: snap.data(),
          error: null,
        });
      } catch (e) {
        if (!alive) return;
        setState({
          loading: false,
          clinicStudyId: null,
          config: null,
          error: "fetch_failed",
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [uid, db, mapping.resolved, mapping.clinicStudyId]);

  return {
    ...state,
    mappingResolved: mapping.resolved,
    mappingLoading: mapping.loading,
    id: mapping.id,
  };
}
